import React, {useContext, useEffect, useState} from "react";
import {AppContextContext, InterestServiceContext, OrganizationServiceContext, VulSourceServiceContext, VulViewServiceContext} from "../../Contexts";
import {Alert, Breadcrumb, Button, Card, Col, Descriptions, Form, Input, message, Modal, Row, Select, Spin} from "antd";
import {LoadingOutlined} from "@ant-design/icons";
import Collapse from "@kunukn/react-collapse";
import * as globalStyles from "../App.module.css";
import {Link, useNavigate, useParams} from "react-router-dom";
import {DocumentTitle} from "../DocumentTitle";
import Interest, {InterestRestrictionType} from "../../domain/Interest";
import {useForm} from "antd/es/form/Form";
import {Permission} from "../../domain/auth/Permission";
import {INTEREST_DELETE_WARNING} from "./InterestDeleteWarning";
import {ServerConstraintViolationsHolder} from "../../sal-ui/ServerConstraintViolations";
import Organization from "../../domain/Organization";
import QueryOptions from "../../sal-ui/QueryOptions";
import {renderOrganizationLink} from "../common/OrganizationLink";
import * as styles from "./InterestDetail.module.css";
import VulSource, {vulSourceSystemLastComparator} from "../../domain/VulSource";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import SelectAllMultiSelect from "../common/SelectAllMultiSelect";
import {definedValues} from "../../utils/SelectOptionUtils";
import VulView, {isManagedVulView, vulViewSystemLastComparator} from "../../domain/VulView";

import InterestMemoryPanel from "./InterestMemoryPanel";

const serverViolationsHolder = new ServerConstraintViolationsHolder();

const DESCRIPTION_EXAMPLES = [
    {
        title: "Infrastructure stack",
        description: `Hardware:
FortiGate
Synology NAS

Software:
Red Hat Enterprise Linux
Rocky Linux
VMware vSphere
Nginx
OpenJDK
PostgreSQL
Kafka`
    },
    {
        title: "Security perimeter",
        description: `Products:
FortiGate
FortiWifi
FortiSandbox
FortiMail

Topics:
VPN vulnerabilities
Firewall management interfaces
Email gateway bypasses
Remote code execution in exposed services`
    },
    {
        title: "Engineering platform",
        description: `Platforms:
TeamCity
Zabbix
OpenJDK
Nginx
PostgreSQL
Kafka

Topics:
CI/CD compromise
Authentication bypass
Privilege escalation
Data exposure`
    }
];

type InterestFormValues = {
    name: string;
    description: string;
    organization?: string;
    restrictionType?: InterestRestrictionType;
    restrictedSourceIds?: string[];
    restrictedViewId?: string;
};

function InterestDetail() {
    const appContext = useContext(AppContextContext);
    const interestService = useContext(InterestServiceContext);
    const organizationService = useContext(OrganizationServiceContext);
    const vulSourceService = useContext(VulSourceServiceContext);
    const vulViewService = useContext(VulViewServiceContext);
    const navigate = useNavigate();
    const {interestId} = useParams<{ interestId: string }>();
    const [interest, setInterest] = useState<Interest | undefined>();
    const hasManage = appContext.user.isSystemAdmin() || appContext.user.hasPermission(Permission.INTEREST__MANAGE_ORG);
    const [editMode, setEditMode] = useState(false);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [vulSources, setVulSources] = useState<VulSource[]>([]);
    const [vulViews, setVulViews] = useState<VulView[]>([]);
    const [descriptionExamplesOpen, setDescriptionExamplesOpen] = useState(false);
    const [formError, setFormError] = useState<string>();
    const [form] = useForm<InterestFormValues>();
    const restrictionType = Form.useWatch("restrictionType", form) ?? "NONE";
    const selectedOrganizationId = Form.useWatch("organization", form) ?? interest?.organization?.id;
    const selectableRestrictedSources = getSelectableRestrictedSources();
    const selectableRestrictedViews = getSelectableRestrictedViews();

    const layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 18},
    };

    const tailLayout = {
        wrapperCol: {offset: 6, span: 18},
    };

    const title = interestId ? (interest?.name || "Interest") : "New interest";

    useEffect(() => {
        vulSourceService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => {
            setVulSources(value.data);
        });

        vulViewService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => {
            setVulViews(value.data);
        });
    }, []);

    useEffect(() => {
        if (appContext.user.isSystemAdmin() && !interestId) {
            organizationService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => {
                setOrganizations(value.data);
            });
        }

        setFormError(undefined);
        setInterest(undefined);

        if (interestId) {
            setEditMode(false);

            reload();

            return;
        }

        form.resetFields();
        setEditMode(true);
    }, [interestId]);

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title: <Link to={"/interests"}>Interests</Link>},
                                {title}
                            ]}
                />

                <Spin
                    spinning={!!interestId && !interest}
                    indicator={<LoadingOutlined style={{fontSize: 24}} spin={true}/>}
                >
                    <h1>{title}</h1>

                    {interestId && hasManage &&
                        <div className={globalStyles["common__top-button-bar"]}>
                            <Button type={"primary"} onClick={() => setEditMode(!editMode)}>
                                Edit
                            </Button>
                            <Button
                                danger
                                style={{marginLeft: 8}}
                                onClick={() => {
                                    Modal.confirm({
                                        content: <>Do you really want to delete this interest? {INTEREST_DELETE_WARNING}</>,
                                        okText: "Delete",
                                        cancelText: "Cancel",
                                        okButtonProps: {danger: true},
                                        onOk: () => onDelete()
                                    });
                                }}>
                                Delete
                            </Button>
                        </div>
                    }

                    <Collapse isOpen={!interestId || editMode}>
                        <h3>{interestId ? "Edit interest" : "New interest"}</h3>

                        <Form
                            {...layout}
                            form={form}
                            initialValues={{restrictionType: "NONE", restrictedSourceIds: []}}
                            onFinish={onFinish}
                            className={`${globalStyles["common__form"]} ${interestId ? globalStyles["common_form--edit"] : globalStyles["common_form--add"]}`}
                        >
                            {formError &&
                                <Alert
                                    type="error"
                                    showIcon={true}
                                    title={formError}
                                    style={{marginBottom: 16}}
                                />}

                            {appContext.user.isSystemAdmin() && !interestId &&
                                <Form.Item
                                    name="organization"
                                    label="Organization"
                                    rules={[{required: true, message: "Organization is required."}]}>
                                    <Select>
                                        {organizations.map(organization => (
                                            <Select.Option key={organization.id} value={organization.id}>
                                                {organization.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            }

                            <Form.Item
                                name="name"
                                label="Name"
                                rules={[
                                    {required: true, message: "Name is required."},
                                    {validator: serverViolationsHolder.createServerValidator('UNIQUE', 'name, organization_id', undefined, {compareLowerCaseValues: true}), message: "Name is already used."}
                                ]}
                                extra="Short, descriptive name of the area you care about (e.g. 'infra'). Used as the AI attribute name — lowercase by convention."
                            >
                                <Input maxLength={100}/>
                            </Form.Item>

                            <Form.Item
                                name="description"
                                label="Description"
                                rules={[{required: true, message: "Description is required."}]}
                                extra={renderDescriptionExtra()}
                            >
                                <Input.TextArea
                                    rows={12}
                                    placeholder={interestId ? undefined : "Describe the products, technologies, platforms, or threat areas that matter to this organization. You can click Show examples to view sample descriptions."}
                                />
                            </Form.Item>

                            <Form.Item
                                name="restrictionType"
                                label="Scope restriction"
                            >
                                <Select>
                                    <Select.Option value="NONE">without restriction</Select.Option>
                                    <Select.Option value="ALLOW_SOURCES">allow selected sources</Select.Option>
                                    <Select.Option value="BLOCK_SOURCES">block selected sources</Select.Option>
                                    <Select.Option value="VIEW">match selected view</Select.Option>
                                </Select>
                            </Form.Item>

                            {isSourceRestrictionType(restrictionType) &&
                                <Form.Item
                                    name="restrictedSourceIds"
                                    label="Sources"
                                    rules={[{required: true, message: "At least one source is required."}]}
                                >
                                    <SelectAllMultiSelect
                                        showSearch={true}
                                        optionFilterProp="children"
                                        placeholder="Select sources"
                                        selectAllValues={definedValues(selectableRestrictedSources, source => source.id)}
                                    >
                                        {renderSourceOptions(selectableRestrictedSources)}
                                    </SelectAllMultiSelect>
                                </Form.Item>
                            }

                            {restrictionType === "VIEW" &&
                                <Form.Item
                                    name="restrictedViewId"
                                    label="View"
                                    rules={[{required: true, message: "View is required."}]}
                                >
                                    <Select
                                        showSearch={true}
                                        optionFilterProp="children"
                                        placeholder="Select view"
                                        allowClear={true}
                                    >
                                        {renderViewOptions(selectableRestrictedViews)}
                                    </Select>
                                </Form.Item>
                            }

                            <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                                <Button type="primary" htmlType="submit">
                                    Save
                                </Button>
                                <Button
                                    style={{marginLeft: 8}}
                                    onClick={() => {
                                        if (interestId) {
                                            setEditMode(false);
                                            if (interest) {
                                                form.setFieldsValue(toFormValues(interest));
                                            }
                                        } else {
                                            navigate("/interests");
                                        }
                                    }}>
                                    Cancel
                                </Button>
                            </Form.Item>
                        </Form>
                    </Collapse>

                    {interestId && interest &&
                        <Descriptions column={1}
                                      bordered={true}
                                      size="small"
                                      className={`${globalStyles.details}`}>
                            {appContext.user.isSystemAdmin() &&
                                <Descriptions.Item label="Organization">
                                    {renderOrganizationLink(interest.organization)}
                                </Descriptions.Item>}
                            <Descriptions.Item label="Name">{interest.name}</Descriptions.Item>
                            <Descriptions.Item label="Description">
                                <span style={{whiteSpace: "pre-wrap"}}>{interest.description}</span>
                            </Descriptions.Item>
                            <Descriptions.Item label="Scope restriction">
                                {renderScopeRestriction(interest)}
                            </Descriptions.Item>
                            {interest.generatedAiAttributeId &&
                                <Descriptions.Item label="Generated AI attribute">
                                    <Link to={`/definitions/ai-attributes/${interest.generatedAiAttributeId}`}>{interest.name}</Link>
                                </Descriptions.Item>}
                            {interest.generatedVulViewId &&
                                <Descriptions.Item label="Generated view">
                                    <Link to={`/vul-source-items/views/${interest.generatedVulViewId}`}>{interest.name}</Link>
                                </Descriptions.Item>}
                            {interest.generatedAiExtractorId &&
                                <Descriptions.Item label="Evaluated by">
                                    <Link to={`/definitions/ai-extractors/${interest.generatedAiExtractorId}`}>Interests AI extractor</Link>
                                </Descriptions.Item>}
                        </Descriptions>
                    }

                    {interestId && interest && <InterestMemoryPanel key={interestId} interestId={interestId} canManage={hasManage}/>}

                    <Modal
                        open={descriptionExamplesOpen}
                        title="Interest examples"
                        width={1100}
                        footer={<Button type="primary" onClick={() => setDescriptionExamplesOpen(false)}>Close</Button>}
                        mask={{closable: false}}
                        onCancel={() => setDescriptionExamplesOpen(false)}
                    >
                        <div className={styles.examplesIntro}>
                            Choose a starting point and adjust it to match the organization. The selected example will replace the current description.
                        </div>

                        <Row gutter={[16, 16]}>
                            {DESCRIPTION_EXAMPLES.map(example => (
                                <Col key={example.title} xs={24} lg={8}>
                                    <Card
                                        className={styles.exampleCard}
                                        size="small"
                                        title={example.title}
                                    >
                                        <pre className={styles.exampleText}>{example.description}</pre>
                                        <Button
                                            className={styles.exampleAction}
                                            type="primary"
                                            ghost={true}
                                            onClick={() => useDescriptionExample(example.description)}
                                        >
                                            Use this example
                                        </Button>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Modal>
                </Spin>
            </>
        </DocumentTitle>
    );

    function reload() {
        if (!interestId) {
            return;
        }

        interestService.get(interestId)
            .then(value => {
                setInterest(value);

                form.setFieldsValue(toFormValues(value));
            });
    }

    function onFinish(values: InterestFormValues) {
        setFormError(undefined);

        const restrictionType = values.restrictionType ?? "NONE";

        const submitValues = {
            ...values,
            restrictionType,
            restrictedSourceIds: isSourceRestrictionType(restrictionType) ? values.restrictedSourceIds : [],
            restrictedViewId: restrictionType === "VIEW" ? values.restrictedViewId : undefined
        };

        if (interestId) {
            interestService.update(interestId, submitValues)
                .then(
                    () => {
                        message.success("Interest updated.");

                        setEditMode(false);

                        reload();
                    },
                    error => handleFormError(error)
                );
        } else {
            interestService.add(submitValues)
                .then(
                    id => {
                        message.success("Interest added.");

                        navigate(`/interests/${id}`);
                    },
                    error => handleFormError(error)
                );
        }
    }

    function handleFormError(error: any) {
        const interestLimitMessage = error?.response?.data?.constraintViolations?.interestLimit?.CUSTOM?.message;
        const restrictedSourceMessage = error?.response?.data?.constraintViolations?.restrictedSourceIds?.CUSTOM?.message;
        const restrictedViewMessage = error?.response?.data?.constraintViolations?.restrictedViewId?.CUSTOM?.message;

        if (interestLimitMessage) {
            setFormError(interestLimitMessage);

            return Promise.resolve();
        }

        if (restrictedSourceMessage) {
            setFormError(restrictedSourceMessage);

            return Promise.resolve();
        }

        if (restrictedViewMessage) {
            setFormError(restrictedViewMessage);

            return Promise.resolve();
        }

        return serverViolationsHolder.handleServerError(error, form);
    }

    function onDelete() {
        if (!interest) {
            return;
        }

        interestService.delete(interest).then(() => {
            message.success("Interest deleted.");

            navigate("/interests");
        });
    }

    function renderDescriptionExtra() {
        return (
            <>
                Describe the area you care about - topics, technologies, products, threat actors, or anything else that defines what's relevant.
                {" "}
                <Button
                    type="link"
                    size="small"
                    style={{padding: 0}}
                    onClick={() => setDescriptionExamplesOpen(true)}
                >
                    Show examples
                </Button>
            </>
        );
    }

    function useDescriptionExample(description: string) {
        form.setFieldsValue({description});

        setDescriptionExamplesOpen(false);
    }

    function renderSourceOptions(selectableSources: VulSource[]) {
        const organizationSources = selectableSources.filter(source => source.owner === ResourceOwnerType.ORGANIZATION);
        const systemSources = selectableSources.filter(source => source.owner === ResourceOwnerType.SYSTEM);

        return (
            <>
                {organizationSources.length > 0 &&
                    <Select.OptGroup label="Organization sources">
                        {organizationSources.map(renderSourceOption)}
                    </Select.OptGroup>}
                {systemSources.length > 0 &&
                    <Select.OptGroup label="System sources">
                        {systemSources.map(renderSourceOption)}
                    </Select.OptGroup>}
            </>
        );
    }

    function getSelectableRestrictedSources() {
        return [...vulSources]
            .sort(vulSourceSystemLastComparator)
            .filter(source =>
                source.owner === ResourceOwnerType.SYSTEM
                || (
                    source.owner === ResourceOwnerType.ORGANIZATION
                    && (!appContext.user.isSystemAdmin() || source.organization?.id === selectedOrganizationId)
                )
            );
    }

    function getSelectableRestrictedViews() {
        return [...vulViews]
            .sort(vulViewSystemLastComparator)
            .filter(view =>
                !isManagedVulView(view)
                && (
                    view.owner === ResourceOwnerType.SYSTEM
                    || (
                        view.owner === ResourceOwnerType.ORGANIZATION
                        && (!appContext.user.isSystemAdmin() || view.organization?.id === selectedOrganizationId)
                    )
                )
            );
    }

    function renderSourceOption(source: VulSource) {
        if (!source.id) {
            return null;
        }

        return (
            <Select.Option key={source.id} value={source.id}>
                {source.name}
            </Select.Option>
        );
    }

    function renderViewOptions(selectableViews: VulView[]) {
        const organizationViews = selectableViews.filter(view => view.owner === ResourceOwnerType.ORGANIZATION);
        const systemViews = selectableViews.filter(view => view.owner === ResourceOwnerType.SYSTEM);

        return (
            <>
                {organizationViews.length > 0 &&
                    <Select.OptGroup label="Organization views">
                        {organizationViews.map(renderViewOption)}
                    </Select.OptGroup>}
                {systemViews.length > 0 &&
                    <Select.OptGroup label="System views">
                        {systemViews.map(renderViewOption)}
                    </Select.OptGroup>}
            </>
        );
    }

    function renderViewOption(view: VulView) {
        if (!view.id) {
            return null;
        }

        return (
            <Select.Option key={view.id} value={view.id}>
                {view.name}
            </Select.Option>
        );
    }

    function renderScopeRestriction(interest: Interest) {
        const type = interest.restrictionType ?? "NONE";

        if (type === "NONE") {
            return <i>without restriction</i>;
        }

        if (type === "VIEW") {
            if (!interest.restrictedView) {
                return <i>no view</i>;
            }

            return (
                <>
                    <div style={{marginBottom: 8}}><i>match selected view:</i></div>
                    {interest.restrictedView.id
                        ? <Link to={`/vul-source-items/views/${interest.restrictedView.id}`}>
                            {interest.restrictedView.name || interest.restrictedView.id}
                        </Link>
                        : interest.restrictedView.name}
                </>
            );
        }

        if (!interest.restrictedSources || interest.restrictedSources.length === 0) {
            return <i>no sources</i>;
        }

        return (
            <>
                <div style={{marginBottom: 8}}><i>{type === "ALLOW_SOURCES" ? "allow selected sources:" : "block selected sources:"}</i></div>
                {interest.restrictedSources.map(source => (
                    <div key={source.id ?? source.name}>
                        {source.id
                            ? <Link to={`/vul-sources/${source.id}`}>{source.name || source.id}</Link>
                            : source.name}
                    </div>
                ))}
            </>
        );
    }

    function toFormValues(interest: Interest): InterestFormValues {
        const restrictedSourceIds = (interest.restrictedSources ?? [])
            .map(source => source.id)
            .filter((id): id is string => !!id);

        return {
            name: interest.name ?? "",
            description: interest.description ?? "",
            organization: interest.organization?.id,
            restrictionType: interest.restrictionType ?? "NONE",
            restrictedSourceIds,
            restrictedViewId: interest.restrictedView?.id
        };
    }

    function isSourceRestrictionType(type: InterestRestrictionType) {
        return type === "ALLOW_SOURCES" || type === "BLOCK_SOURCES";
    }
}

export default InterestDetail;
